import React, { useRef, useState, useEffect } from 'react';
import { uniq } from 'lodash-es';
import { formatOptions } from '@gp/components/Select/utils';
import { getOptionValues } from '@gp/components/SelectMultiple/utils';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { GROUP_KEY } from '@gp/constants/select';

import type { SelectOptionWithGroup, ChangeContext } from 'tea-component';

export default function useSearchSelect(props: {
  // 是否处于搜索模式
  searchMode: boolean;
  searchValue: string;
  filter: (inputValue: string, option: SelectOptionWithGroup) => boolean;
  optionTextRenderer?: IGPSelectOptionTextRenderer;
  options: IGPSelectOptionWithGroup[] | null;
  value: string[];
  setValue: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { searchMode, searchValue, filter, optionTextRenderer, options, value, setValue } = props || {};

  const isSelectAll = !!options?.length && value?.length === options?.length;

  const allSearchingValues = useRef<string[]>([]);

  const [searchingOptions, setSearchingOptions] = useState<IGPSelectOptionWithGroup[]>(options || []);
  const [searchingValue, setSearchingValue] = useState<string[]>(value || []);

  const isSearchingSelectAll = !!searchingOptions?.length && searchingValue?.length === searchingOptions?.length;

  const updateSearchState = (payload: string) => {
    if (!searchMode) return;
    let searchingOptionsTmp = options?.filter((option) => filter?.(payload, option) ?? true) || [];
    searchingOptionsTmp = formatOptions(searchingOptionsTmp, {
      searchable: true,
      searchValue: payload,
      optionTextRenderer,
    });
    setSearchingOptions(searchingOptionsTmp);

    allSearchingValues.current = getOptionValues(searchingOptionsTmp);
    const searchingValueTmp = isSelectAll ? [] : value?.filter((item) => allSearchingValues.current?.includes(item));
    setSearchingValue(searchingValueTmp);
  };

  const handleChangeWhenSearching = (
    payload: string[],
    context: ChangeContext<React.SyntheticEvent<Element, Event>> | null,
  ) => {
    if (!searchMode) return;
    // @ts-ignore
    const { name: checkContextName, value: checkContextValue } = context?.check || {};
    if (!checkContextName) return;
    if (!!checkContextValue) {
      const searchingValueTmp = [checkContextName, ...searchingValue];
      const valueTmp = isSelectAll ? [checkContextName] : [checkContextName, ...value];
      setSearchingValue(searchingValueTmp);
      setValue(valueTmp);
    } else {
      const searchingValueTmp = searchingValue?.filter((item) => item !== checkContextName) || [];
      const valueTmp = value?.filter((item) => item !== checkContextName) || [];
      setSearchingValue(searchingValueTmp);
      setValue(valueTmp);
    }
  };

  const handleSelectAll = () => {
    if (!searchMode) return;
    if (!Array.isArray(allSearchingValues.current) || !allSearchingValues.current.length) return;
    const valueTmp = isSelectAll ? [...allSearchingValues.current] : uniq([...allSearchingValues.current, ...value]);
    setValue(valueTmp);
    setSearchingValue([...allSearchingValues.current]);
  };

  const handleClearAll = () => {
    if (!searchMode) return;
    if (!Array.isArray(allSearchingValues.current) || !allSearchingValues.current.length) return;
    const valueTmp = value?.filter((item) => !allSearchingValues.current?.includes(item));
    setValue(valueTmp);
    setSearchingValue([]);
  };

  const searchingGroups = {
    [GROUP_KEY]: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{CONSTANT_TEXTS.SELECT_SEPARATED_GROUP_NAME}</span>
        {searchingOptions?.length > 0 && (
          <span
            className="dbg-text-highlight dbg-cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              if (isSearchingSelectAll) {
                handleClearAll();
              } else {
                handleSelectAll();
              }
            }}
          >
            {isSearchingSelectAll ? 'Clear All' : 'Select All'}
          </span>
        )}
      </div>
    ),
  };

  useEffect(() => {
    if (!searchMode) {
      setSearchingOptions([]);
      setSearchingValue([]);
      return;
    }

    updateSearchState(searchValue);
  }, [searchMode, searchValue]);

  return {
    searchingGroups,
    searchingOptions,
    setSearchingOptions,
    searchingValue,
    setSearchingValue,
    onChangeWhenSearching: handleChangeWhenSearching,
  };
}
